import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import { DIGITAL_LOAN_STEPS } from 'constants/digitalLoan';
import { CONTRACT_DOCUMENT_TYPES, PHASE4_ERROR_CODES } from 'constants/digitalLoanPhase4';
import {
  acceptOffer,
  captureClabe,
  generateContract,
  getContractDocument,
  getDigitalLoanStatus,
  getLoanTerms,
  getOffer,
  getSimulationDocument,
  registerBiometricFinal,
  rejectOffer,
} from 'modules/digitalLoan/actions';
import { verifyIdMissionSelfie } from 'modules/session/actions';
import { ROUTE_PATH } from 'routes';
import useLiterals from 'utils/hooks/useLiterals';
import LegalDocumentViewer from './LegalDocumentViewer';
import FinalOfferScreen from './FinalOfferScreen';
import ClabeScreen from './ClabeScreen';
import BiometricFinalScreen from './BiometricFinalScreen';
import OfferRejectedScreen from './OfferRejectedScreen';
import RejectOfferScreen from './RejectOfferScreen';
import ContractGenerationScreen from './ContractGenerationScreen';
import OperationalWindowScreen from './OperationalWindowScreen';
import ContractReadyScreen from './ContractReadyScreen';
import { decodePdfBase64 } from '../utils/decodePdfBase64';
import {
  clabeFeedbackKey,
  classifyClabeOutcome,
  readPhase4Error,
  resolveContractGenerationOutcome,
  routePhase4Error,
} from '../utils/phase4Flow';
import { defaultLoanSelection } from '../utils/loanTerms';
import {
  isPhase3Closed,
  resolvePhase4Entry,
  screenForPhase4Step,
} from '../utils/phase4Routing';

const Phase4Wizard = ({
  step,
  applicationId,
  onPhase3,
  onHome,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const literals = useLiterals('digitalLoan');
  const phase4 = useMemo(() => literals.phase4 || {}, [literals]);
  const [offer, setOffer] = useState(null);
  const [offerNotice, setOfferNotice] = useState('');
  const [offerLoading, setOfferLoading] = useState(false);
  const offerRequestRef = useRef(0);
  const [submitting, setSubmitting] = useState(false);
  const [clabeMessage, setClabeMessage] = useState('');
  const [clabeTone, setClabeTone] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectBlocked, setRejectBlocked] = useState('');
  const [offerRejected, setOfferRejected] = useState(false);
  const [contractMessage, setContractMessage] = useState('');
  const [pendingUntil, setPendingUntil] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [openingTipo, setOpeningTipo] = useState('');
  const [activeDocument, setActiveDocument] = useState(null);
  const [technicalError, setTechnicalError] = useState(null);

  const goTo = useCallback((nextStep) => {
    if (!nextStep || nextStep === step) return;
    navigate(ROUTE_PATH.setDigitalLoan(nextStep));
  }, [navigate, step]);

  const applyRoute = useCallback((routed) => {
    if (!routed) return false;
    if (routed.type === 'go-phase3') {
      onPhase3();
      return true;
    }
    if (routed.type === 'go-clabe') {
      goTo(DIGITAL_LOAN_STEPS.CLABE);
      return true;
    }
    if (routed.type === 'go-biometric') {
      goTo(DIGITAL_LOAN_STEPS.BIOMETRIC_FINAL);
      return true;
    }
    if (routed.type === 'go-ready') {
      goTo(DIGITAL_LOAN_STEPS.CONTRACT_READY);
      return true;
    }
    if (routed.type === 'go-waiting') {
      setPendingUntil(routed.pendingUntil || '');
      goTo(DIGITAL_LOAN_STEPS.OPERATIONAL_WINDOW);
      return true;
    }
    if (routed.type === 'contract-retry') {
      setContractMessage(
        routed.code === PHASE4_ERROR_CODES.CONTRACT_NOT_GENERATED
          ? phase4.contract?.notGenerated
          : phase4.contract?.failed,
      );
      goTo(DIGITAL_LOAN_STEPS.CONTRACT_GENERATION);
      return true;
    }
    if (routed.type === 'document-error') {
      setDocumentError(phase4.ready?.documentInvalid || '');
      return true;
    }
    if (routed.type === 'modal') {
      setTechnicalError(routed.code ? { data: { error: routed.code } } : true);
      return true;
    }
    return false;
  }, [goTo, onPhase3, phase4]);

  const requestOffer = useCallback(async (query) => {
    if (!applicationId) return null;
    const requestId = offerRequestRef.current + 1;
    offerRequestRef.current = requestId;
    setOfferLoading(true);
    setOffer(null);
    try {
      const nextOffer = await dispatch(getOffer(applicationId, query || {}));
      if (requestId !== offerRequestRef.current) return null;
      setOffer(nextOffer);
      if (nextOffer?.pendingUntil) {
        setPendingUntil(nextOffer.pendingUntil);
      }
      return nextOffer;
    } catch (error) {
      if (requestId !== offerRequestRef.current) return null;
      const routed = routePhase4Error(error);
      if (routed.type !== 'refetch-offer') applyRoute(routed);
      return null;
    } finally {
      if (requestId === offerRequestRef.current) {
        setOfferLoading(false);
      }
    }
  }, [applicationId, applyRoute, dispatch]);

  const loadFinalOffer = useCallback(async (nextSelection) => {
    let activeSelection = nextSelection;
    if (!activeSelection) {
      const terms = await dispatch(getLoanTerms());
      activeSelection = defaultLoanSelection(terms);
    }
    if (!activeSelection) {
      setOffer(null);
      return null;
    }
    return requestOffer({
      plazo: activeSelection.term,
      periodicidad: activeSelection.frequency,
    });
  }, [dispatch, requestOffer]);

  useEffect(() => {
    if (step !== DIGITAL_LOAN_STEPS.FINAL_OFFER) return undefined;
    let cancelled = false;
    loadFinalOffer().catch((error) => {
      if (!cancelled) applyRoute(routePhase4Error(error));
    });
    return () => {
      cancelled = true;
    };
    // Consulta tarifario y oferta al entrar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, step]);

  useEffect(() => {
    if (step !== DIGITAL_LOAN_STEPS.OPERATIONAL_WINDOW || pendingUntil) {
      return undefined;
    }
    requestOffer();
    return undefined;
    // Una lectura de pendingUntil al reentrar. Sin polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, pendingUntil, step]);

  const describeClabeFailure = (outcome) => {
    if (outcome?.type === 'clabe-invalid') {
      setClabeTone('invalid');
      const key = clabeFeedbackKey(outcome);
      setClabeMessage(phase4.clabe?.[key] || phase4.clabe?.invalid || '');
      return 'invalid';
    }
    setClabeTone('retryable');
    setClabeMessage(phase4.clabe?.unavailable || '');
    return 'retryable';
  };

  const handleAccept = async () => {
    if (!offer || submitting || offerLoading || offerRejected) return;
    setSubmitting(true);
    setOfferNotice('');
    try {
      const response = await dispatch(acceptOffer(applicationId, offer));
      const next = screenForPhase4Step(response.currentStep)
        || DIGITAL_LOAN_STEPS.CLABE;
      goTo(next);
    } catch (error) {
      const routed = routePhase4Error(error);
      if (routed.type === 'refetch-offer') {
        setOfferNotice(
          routed.code === PHASE4_ERROR_CODES.OFFER_EXPIRED
            ? phase4.offer?.expiredNotice
            : phase4.offer?.mismatchNotice,
        );
        await loadFinalOffer();
        return;
      }
      applyRoute(routed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyClabe = async (clabe) => {
    if (submitting || !applicationId || offerRejected) return 'invalid';
    setSubmitting(true);
    setClabeMessage('');
    setClabeTone('');
    try {
      const response = await dispatch(captureClabe(applicationId, clabe));
      const outcome = classifyClabeOutcome({ response });
      if (outcome.type === 'validated') {
        setClabeTone('validated');
        setClabeMessage(phase4.clabe?.validated || '');
        return 'validated';
      }
      return describeClabeFailure(outcome);
    } catch (error) {
      const outcome = classifyClabeOutcome({ error });
      return describeClabeFailure(outcome);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueValidatedClabe = () => {
    if (offerRejected) return;
    goTo(DIGITAL_LOAN_STEPS.BIOMETRIC_FINAL);
  };

  const handleCaptured = async (capture) => {
    if (submitting || offerRejected) return;
    setSubmitting(true);
    try {
      const response = await dispatch(
        registerBiometricFinal(applicationId, capture),
      );
      goTo(
        screenForPhase4Step(response.currentStep)
        || DIGITAL_LOAN_STEPS.CONTRACT_GENERATION,
      );
    } catch (error) {
      applyRoute(routePhase4Error(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    if (submitting || offerRejected) return;
    setSubmitting(true);
    setContractMessage('');
    try {
      const response = await dispatch(generateContract(applicationId));
      const outcome = resolveContractGenerationOutcome(response);
      if (outcome.type === 'ready') {
        goTo(DIGITAL_LOAN_STEPS.CONTRACT_READY);
        return;
      }
      if (outcome.type === 'waiting') {
        setPendingUntil(outcome.pendingUntil || '');
        goTo(DIGITAL_LOAN_STEPS.OPERATIONAL_WINDOW);
        return;
      }
      setContractMessage(phase4.contract?.notGenerated || '');
    } catch (error) {
      applyRoute(routePhase4Error(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const status = await dispatch(getDigitalLoanStatus());
      const next = resolvePhase4Entry({ status })
        || (isPhase3Closed(status) ? DIGITAL_LOAN_STEPS.FINAL_OFFER : null);
      if (next && next !== DIGITAL_LOAN_STEPS.OPERATIONAL_WINDOW) {
        goTo(next);
      } else if (status?.activeApplication?.currentStep) {
        const screen = screenForPhase4Step(status.activeApplication.currentStep);
        if (screen) goTo(screen);
      }
    } catch (error) {
      applyRoute(routePhase4Error(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenSimulationDocument = async (document) => {
    if (openingTipo || !document || String(document.tipo) === '01') return;
    setDocumentError('');
    setOpeningTipo(document.id);
    try {
      const result = await dispatch(
        getSimulationDocument(applicationId, document.tipo),
      );
      setActiveDocument({
        id: document.id,
        title: phase4.offer?.documents?.[document.literalKey] || document.id,
        file: { data: decodePdfBase64(result.file) },
      });
    } catch (error) {
      const routed = routePhase4Error(error);
      if (routed.type === 'document-error') {
        setDocumentError(phase4.ready?.documentInvalid || '');
      } else {
        applyRoute(routed);
      }
    } finally {
      setOpeningTipo('');
    }
  };

  const handleOpenDocument = async (tipo) => {
    if (openingTipo) return;
    setDocumentError('');
    setOpeningTipo(tipo);
    try {
      const document = await dispatch(getContractDocument(applicationId, tipo));
      const documentType = CONTRACT_DOCUMENT_TYPES.find(
        (item) => item.tipo === tipo,
      );
      setActiveDocument({
        id: tipo,
        title: phase4.ready?.documents?.[documentType?.literalKey] || tipo,
        file: { data: decodePdfBase64(document.file) },
      });
    } catch (error) {
      const routed = routePhase4Error(error);
      if (routed.type === 'document-error') {
        setDocumentError(phase4.ready?.documentInvalid || '');
      } else {
        applyRoute(routed);
      }
    } finally {
      setOpeningTipo('');
    }
  };

  const handleRejectConfirm = async () => {
    if (submitting || offerRejected || !applicationId) return;
    setSubmitting(true);
    setRejectBlocked('');
    try {
      const response = await dispatch(rejectOffer(applicationId));
      if (response?.status !== 'REJECTED') {
        setRejectBlocked(phase4.reject?.failed || '');
        return;
      }
      setOfferRejected(true);
    } catch (error) {
      const code = readPhase4Error(error).code;
      setRejectBlocked(
        code === PHASE4_ERROR_CODES.OFFER_NOT_AVAILABLE
          ? (phase4.reject?.notAvailable || '')
          : (phase4.reject?.failed || ''),
      );
    } finally {
      setSubmitting(false);
    }
  };

  let screen = null;
  if (offerRejected) {
    screen = (
      <OfferRejectedScreen
        literals={phase4.reject || {}}
        onClose={onHome}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.FINAL_OFFER && rejectOpen) {
    screen = (
      <RejectOfferScreen
        literals={phase4.reject || {}}
        blockedMessage={rejectBlocked}
        onConfirm={handleRejectConfirm}
        onBack={() => {
          setRejectBlocked('');
          setRejectOpen(false);
        }}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.FINAL_OFFER) {
    screen = (
      <FinalOfferScreen
        literals={phase4.offer || {}}
        offer={offer}
        notice={offerNotice}
        documentError={documentError}
        isLoading={offerLoading}
        isSubmitting={submitting}
        openingDocumentId={openingTipo}
        onOpenDocument={handleOpenSimulationDocument}
        onAccept={handleAccept}
        onReject={() => setRejectOpen(true)}
        onRetry={() => loadFinalOffer()}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.CLABE) {
    screen = (
      <ClabeScreen
        literals={phase4.clabe || {}}
        message={clabeMessage}
        messageTone={clabeTone}
        isSubmitting={submitting}
        onVerify={handleVerifyClabe}
        onContinue={handleContinueValidatedClabe}
        onBack={() => goTo(DIGITAL_LOAN_STEPS.FINAL_OFFER)}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.BIOMETRIC_FINAL) {
    screen = (
      <BiometricFinalScreen
        literals={phase4.biometric || {}}
        isSubmitting={submitting}
        onCaptured={handleCaptured}
        onVerifySelfie={(selfie) => dispatch(verifyIdMissionSelfie(selfie))}
        onBack={() => goTo(DIGITAL_LOAN_STEPS.CLABE)}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.CONTRACT_GENERATION) {
    screen = (
      <ContractGenerationScreen
        literals={phase4.contract || {}}
        message={contractMessage}
        isSubmitting={submitting}
        onGenerate={handleGenerate}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.OPERATIONAL_WINDOW) {
    screen = (
      <OperationalWindowScreen
        literals={phase4.window || {}}
        pendingUntil={pendingUntil || offer?.pendingUntil || ''}
        isRefreshing={submitting}
        onRefresh={handleRefreshStatus}
      />
    );
  } else if (step === DIGITAL_LOAN_STEPS.CONTRACT_READY) {
    screen = (
      <ContractReadyScreen
        literals={phase4.ready || {}}
        documentError={documentError}
        openingTipo={openingTipo}
        onOpenDocument={handleOpenDocument}
        onHome={onHome}
      />
    );
  }

  return (
    <>
      {screen}
      {activeDocument && (
        <LegalDocumentViewer
          document={activeDocument}
          onClose={() => setActiveDocument(null)}
        />
      )}
      {technicalError && (
        <ErrorModal
          description={technicalError}
          onClose={() => setTechnicalError(null)}
        />
      )}
    </>
  );
};

Phase4Wizard.propTypes = {
  step: PropTypes.string.isRequired,
  applicationId: PropTypes.string,
  onPhase3: PropTypes.func.isRequired,
  onHome: PropTypes.func.isRequired,
};

export default Phase4Wizard;
